import { hash } from './hash';

/**
 * A restaurant's storefront in FirstMango Town: what passers-by see from the street. Designed by the owner (like
 * the floor plan); the same rules are checked by the API.
 */
export type FrontStyle = 'shophouse' | 'cafe' | 'stall' | 'wooden' | 'modern' | 'tent' | 'truck';
export type Awning = 'none' | 'flat' | 'stripes' | 'scallop';
export type SignStyle = 'board' | 'neon' | 'lightbox';
export type FrontExtra = 'plants' | 'lanterns' | 'seats' | 'menuboard' | 'bike' | 'lights';

export interface Storefront {
  style: FrontStyle;
  /** Width in shophouse units (4 m each). */
  lots: 1 | 2 | 3;
  wall: string;
  accent: string;
  awning: Awning;
  sign: { text: string; sub?: string; style: SignStyle; color: string };
  extras: FrontExtra[];
}

/** One shophouse unit is 4 m wide. */
export const LOT_W = 4;
export const SIGN_MAX = 24;
export const SUB_MAX = 32;

export const STYLES: Record<FrontStyle, { th: string; en: string; floors: number }> = {
  shophouse: { th: 'ตึกแถว', en: 'Shophouse', floors: 3 },
  cafe: { th: 'คาเฟ่', en: 'Café', floors: 2 },
  stall: { th: 'ร้านริมทาง', en: 'Street stall', floors: 1 },
  wooden: { th: 'เรือนไม้', en: 'Wooden house', floors: 2 },
  modern: { th: 'โมเดิร์น', en: 'Modern', floors: 2 },
  tent: { th: 'เต็นท์ตลาด', en: 'Market tent', floors: 1 },
  truck: { th: 'ฟู้ดทรัค', en: 'Food truck', floors: 1 },
};

export const AWNINGS: Record<Awning, { th: string; en: string }> = {
  none: { th: 'ไม่มี', en: 'None' },
  flat: { th: 'กันสาด', en: 'Flat' },
  stripes: { th: 'ลายทาง', en: 'Stripes' },
  scallop: { th: 'ระบาย', en: 'Scallop' },
};

export const SIGNS: Record<SignStyle, { th: string; en: string }> = {
  board: { th: 'ป้ายไม้', en: 'Board' },
  neon: { th: 'นีออน', en: 'Neon' },
  lightbox: { th: 'กล่องไฟ', en: 'Lightbox' },
};

export const EXTRAS: Record<FrontExtra, { th: string; en: string }> = {
  plants: { th: 'ต้นไม้', en: 'Plants' },
  lanterns: { th: 'โคมแดง', en: 'Lanterns' },
  seats: { th: 'โต๊ะหน้าร้าน', en: 'Street seats' },
  menuboard: { th: 'ป้ายเมนู', en: 'Menu board' },
  bike: { th: 'มอเตอร์ไซค์ส่งของ', en: 'Delivery bike' },
  lights: { th: 'ไฟประดับ', en: 'String lights' },
};

/** Pastel shophouse walls and stronger accents (shutters, awnings, frames), all readable with dark sign text. */
export const WALLS = ['#F3E3C3', '#F6D6C8', '#D8E8D2', '#CFE0EE', '#F2EDE4', '#E9D3E8', '#F7E6A8', '#E3C9A6'];
export const ACCENTS = ['#2F7D5B', '#C2412D', '#2F5FA8', '#E2A21B', '#7A4E2D', '#3A3F4B', '#D9617A', '#1F8C8C'];
export const SIGN_COLORS = ['#FFB21E', '#FF5A6E', '#4FD1C5', '#7CC6FF', '#FFFFFF', '#B78CFF', '#7BE38E'];

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** A starting storefront for a restaurant that has not designed one: varied by name so a street is not uniform. */
export function defaultStorefront(name: string): Storefront {
  const h = hash(name || 'shop');
  const style = (['shophouse', 'shophouse', 'cafe', 'stall', 'wooden', 'modern'] as const)[h % 6];
  return {
    style,
    lots: style === 'stall' ? 1 : ((h >>> 3) % 3 === 0 ? 2 : 1),
    wall: WALLS[(h >>> 5) % WALLS.length],
    accent: ACCENTS[(h >>> 9) % ACCENTS.length],
    awning: style === 'modern' ? 'none' : (['flat', 'stripes', 'scallop'] as const)[(h >>> 13) % 3],
    sign: { text: clip(name || 'ร้านอาหาร', SIGN_MAX), style: style === 'modern' ? 'lightbox' : style === 'cafe' ? 'neon' : 'board', color: SIGN_COLORS[(h >>> 17) % SIGN_COLORS.length] },
    extras: style === 'stall' ? ['seats', 'lights'] : style === 'wooden' ? ['plants', 'lanterns'] : ['plants', 'menuboard'],
  };
}

const clip = (s: string, n: number) => Array.from(s.trim()).slice(0, n).join('');

/** Checks an owner's storefront; returns the problems (empty = fine). */
export function storefrontErrors(x: unknown): string[] {
  const e: string[] = [];
  if (!x || typeof x !== 'object') return ['storefront must be an object'];
  const f = x as Record<string, unknown>;
  if (!(typeof f.style === 'string' && f.style in STYLES)) e.push('style is not one of the storefront styles');
  if (![1, 2, 3].includes(f.lots as number)) e.push('lots must be 1, 2 or 3');
  for (const k of ['wall', 'accent'] as const) if (!(typeof f[k] === 'string' && HEX.test(f[k] as string))) e.push(`${k} must be a #RRGGBB colour`);
  if (!(typeof f.awning === 'string' && f.awning in AWNINGS)) e.push('awning is not one of the awning styles');
  const s = f.sign as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') e.push('sign is required');
  else {
    if (typeof s.text !== 'string' || !s.text.trim() || Array.from(s.text).length > SIGN_MAX) e.push(`sign.text must be 1-${SIGN_MAX} characters`);
    if (s.sub !== undefined && (typeof s.sub !== 'string' || Array.from(s.sub).length > SUB_MAX)) e.push(`sign.sub must be at most ${SUB_MAX} characters`);
    if (!(typeof s.style === 'string' && s.style in SIGNS)) e.push('sign.style is not one of the sign styles');
    if (!(typeof s.color === 'string' && HEX.test(s.color))) e.push('sign.color must be a #RRGGBB colour');
  }
  if (!Array.isArray(f.extras) || f.extras.length > 6 || f.extras.some((v) => !(typeof v === 'string' && v in EXTRAS)) || new Set(f.extras).size !== f.extras.length) {
    e.push('extras must be a list of distinct extras');
  }
  return e;
}

/** Width of the storefront on the street, in metres. */
export const frontWidth = (f: Pick<Storefront, 'lots'>) => f.lots * LOT_W;
